import { ReactNode, useState, useEffect, useRef } from 'react'
import './Accordion.css'

export interface AccordionSectionConfig {
  id: string
  title: string
  description?: string
  initiallyOpen?: boolean
}

interface AccordionProps {
  sections: AccordionSectionConfig[]
  renderSection: (sectionId: string) => ReactNode
  openSectionFromHash?: string
}

export function Accordion({ sections, renderSection, openSectionFromHash }: AccordionProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    sections.forEach((section) => {
      if (section.initiallyOpen) {
        initial.add(section.id)
      }
    })
    return initial
  })
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const hasScrolledRef = useRef(false)
  const hashProcessedRef = useRef<string | null>(null)

  // Handle hash-based navigation
  useEffect(() => {
    if (openSectionFromHash && hashProcessedRef.current !== openSectionFromHash) {
      const sectionId = openSectionFromHash.replace('#', '')
      if (sections.some((s) => s.id === sectionId)) {
        hashProcessedRef.current = openSectionFromHash
        const wasAlreadyOpen = openSections.has(sectionId)
        
        setOpenSections((prev) => {
          const next = new Set(prev)
          next.add(sectionId)
          return next
        })
        
        // Only scroll if section wasn't already open, and wait longer for content to render
        if (!wasAlreadyOpen && !hasScrolledRef.current) {
          setTimeout(() => {
            const element = sectionRefs.current[sectionId]
            if (element) {
              // Use manual scroll calculation to avoid aggressive scrolling
              const headerOffset = 80 // Account for any fixed headers
              const elementPosition = element.getBoundingClientRect().top
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset
              
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              })
              hasScrolledRef.current = true
            }
          }, 300) // Increased delay to ensure form is fully rendered
        }
      }
    }
  }, [openSectionFromHash, sections, openSections])

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div className="accordion" role="presentation">
      {sections.map((section) => {
        const isOpen = openSections.has(section.id)
        const buttonId = `${section.id}-toggle`
        const panelId = `${section.id}-panel`

        return (
          <div 
            key={section.id} 
            id={section.id}
            className="accordion-section"
            ref={(el) => {
              sectionRefs.current[section.id] = el
            }}
          >
            <h2 className="accordion-header">
              <button
                id={buttonId}
                type="button"
                className="accordion-button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggleSection(section.id)}
              >
                <span className="accordion-title">{section.title}</span>
                {section.description && (
                  <span className="accordion-description">{section.description}</span>
                )}
                <span className="accordion-icon" aria-hidden="true">
                  {isOpen ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
              </button>
            </h2>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              className={`accordion-panel ${isOpen ? 'open' : 'collapsed'}`}
            >
              {isOpen && (
                <div className="accordion-panel-inner">
                  {renderSection(section.id)}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

